import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { Review } from '../../interfaces/Review/Review';
import { deleteReview, getReview, updateReview } from '../../api/Review';
import { sharedStyles } from '../../utils/Style';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { Waiting } from '../../components/Waiting/Waiting';
import NumberInput from '../../components/NumberInput/NumberInput';

export const UpdateReviewPage = () => {
  const navigate = useNavigate();

  const { reviewID: reviewIDAsStr } = useParams<string>();

  const reviewID = reviewIDAsStr ? Number(reviewIDAsStr) : undefined;

  const [review, setReview] = useState<Review | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [clickedDelete, setClickedDelete] = useState(false);

  const refreshReview = () => {
    setLoading(true);
    getReview(reviewID!)
      .then((r) => setReview(r))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshReview();
  }, [reviewIDAsStr]);

  const handleSave = () => {
    if (!review) {
      return;
    }

    setLoading(true);
    updateReview(review)
      .then(() => {
        toast.success('Değerlendirme başarıyla güncellendi');
        refreshReview();
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  const handleDelete = () => {
    setLoading(true);
    deleteReview(reviewID!)
      .then(() => {
        toast.success('Değerlendirme başarıyla silindi');
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const goBack = () => navigate('/products/reviews');

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili değerlendirmeyi silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={handleDelete}
          color='error'
        />
      )}
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>Değerlendirmeyi Düzenle</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant='contained'
          color='error'
          size='small'
          startIcon={<DeleteIcon />}
          onClick={() => setClickedDelete(true)}
          sx={{ ...sharedStyles.buttonText }}
        >
          Sil
        </Button>
      </Box>

      {!review && !loading && (
        <Typography>Değerlendirme yüklenirken bir hata oluştu</Typography>
      )}

      {review && (
        <Paper
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: 400,
            height: 'fit-content',
            p: 3,
            gap: 1.5,
          }}
        >
          <TextField
            label='Kullanıcı'
            variant='outlined'
            size='small'
            value={review.userName}
            disabled
            fullWidth
          />

          <Tooltip title={review.productName}>
            <TextField
              label='Ürün'
              variant='outlined'
              size='small'
              value={review.productName}
              disabled
              fullWidth
            />
          </Tooltip>

          <TextField
            label='Yorum'
            variant='outlined'
            size='small'
            multiline
            value={review.comment}
            onChange={(e) => setReview({ ...review, comment: e.target.value })}
            fullWidth
          />

          <NumberInput
            label='Puan'
            value={review.rating}
            onChange={(newRating) =>
              setReview({ ...review, rating: newRating })
            }
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexGrow: 1,
              alignItems: 'center',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={review.isActive}
                  onChange={() =>
                    setReview({ ...review, isActive: !review.isActive })
                  }
                />
              }
              label={<Typography sx={sharedStyles.subtitle2}>Aktif</Typography>}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant='contained'
              onClick={handleSave}
              sx={{ ...sharedStyles.buttonText }}
            >
              Kaydet
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};
